import { useEffect, useRef, useState, type ReactNode } from "react";

type Props = {
  width?: number;
  height?: number;
  revealThreshold?: number; // 0..1
  onRevealed?: () => void;
  onScratchStart?: () => void;
  onFallbackReveal?: () => void;
  fallbackLabel?: string;
  children: ReactNode; // content underneath
  brushRadius?: number;
};

/**
 * ScratchCard — canvas overlay the user scratches with pointer/touch
 * to reveal the children beneath. Fires onRevealed once threshold is crossed,
 * then auto-clears the overlay with a fade.
 */
export function ScratchCard({
  width = 280,
  height = 140,
  revealThreshold = 0.55,
  onRevealed,
  onScratchStart,
  onFallbackReveal,
  fallbackLabel = "Reveal code",
  children,
  brushRadius = 24,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDown = useRef(false);
  const startedRef = useRef(false);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);
  const [revealed, setRevealed] = useState(false);
  const revealedRef = useRef(false);

  const notifyStart = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    onScratchStart?.();
  };

  const revealNow = (viaFallback: boolean) => {
    if (revealedRef.current) return;
    revealedRef.current = true;
    setRevealed(true);
    onRevealed?.();
    if (viaFallback) onFallbackReveal?.();
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    // Foil gradient
    const grad = ctx.createLinearGradient(0, 0, width, height);
    grad.addColorStop(0, "#c9d3e6");
    grad.addColorStop(0.5, "#8896b3");
    grad.addColorStop(1, "#c9d3e6");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, height);

    // Sparkle noise
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    for (let i = 0; i < 40; i++) {
      ctx.beginPath();
      ctx.arc(Math.random() * width, Math.random() * height, Math.random() * 1.4, 0, Math.PI * 2);
      ctx.fill();
    }

    // Label
    ctx.fillStyle = "rgba(30,40,60,0.85)";
    ctx.font = "600 12px system-ui, -apple-system, Segoe UI, Roboto";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("SCRATCH TO REVEAL", width / 2, height / 2);
  }, [width, height]);

  const getPoint = (e: PointerEvent | React.PointerEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e as PointerEvent).clientX - rect.left) * (width / rect.width),
      y: ((e as PointerEvent).clientY - rect.top) * (height / rect.height),
    };
  };

  const scratchLine = (from: { x: number; y: number }, to: { x: number; y: number }) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.globalCompositeOperation = "destination-out";
    ctx.lineWidth = brushRadius * 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
    ctx.globalCompositeOperation = "source-over";
  };

  const checkReveal = () => {
    if (revealedRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const step = 8;
    const img = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let clear = 0;
    let total = 0;
    for (let i = 3; i < img.length; i += 4 * step) {
      total++;
      if (img[i] < 32) clear++;
    }
    if (clear / total >= revealThreshold) {
      revealNow(false);
    }
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (revealedRef.current) return;
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
    isDown.current = true;
    notifyStart();
    lastPoint.current = getPoint(e);
    scratchLine(lastPoint.current, lastPoint.current);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!isDown.current || revealedRef.current) return;
    const p = getPoint(e);
    scratchLine(lastPoint.current ?? p, p);
    lastPoint.current = p;
  };
  const onPointerUp = () => {
    if (!isDown.current) return;
    isDown.current = false;
    lastPoint.current = null;
    checkReveal();
  };

  return (
    <div className="mx-auto flex flex-col items-center gap-2" style={{ width }}>
      <div
        className="relative select-none overflow-hidden rounded-xl border-2 border-dashed border-[color:var(--brand,#1E5EFF)] bg-[color:var(--brand,#1E5EFF)]/5"
        style={{ width, height }}
      >
        <div
          className="absolute inset-0 flex items-center justify-center px-3 text-center"
          aria-live="polite"
        >
          {children}
        </div>
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={
            revealed
              ? "Coupon revealed"
              : "Scratch card. Drag to reveal, or use the Reveal code button below."
          }
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{
            width,
            height,
            touchAction: "none",
            cursor: revealed ? "default" : "grab",
            opacity: revealed ? 0 : 1,
            transition: "opacity 400ms ease",
            pointerEvents: revealed ? "none" : "auto",
          }}
          className="absolute inset-0"
        />
      </div>
      {!revealed ? (
        <button
          type="button"
          onClick={() => {
            notifyStart();
            revealNow(true);
          }}
          className="text-[11px] font-semibold uppercase tracking-wider text-[color:var(--brand,#1E5EFF)] underline underline-offset-2 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand,#1E5EFF)]/40 focus-visible:ring-offset-2 rounded"
        >
          Can't scratch? {fallbackLabel}
        </button>
      ) : null}
    </div>
  );
}
